// Complaint detail (ISO 9001 clause 10.2). Inline-editable fields, a forward-only status
// workflow (open -> investigating -> resolved -> closed; a close requires a corrective
// action), an overdue warning, and a delete control (hard-delete an untouched open
// complaint, otherwise archive). Office/admin.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  api,
  ApiError,
  type ComplaintDetail,
  type ComplaintSource,
  type ComplaintSeverity,
  type ComplaintStatus,
  type ProspectOwner,
} from "../../lib/api";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { useOptionsLoader } from "../../lib/useOptionsLoader";
import { RecordPicker } from "../../components/RecordPicker";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { RecordHeader } from "../../components/RecordHeader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import {
  SOURCE_LABEL,
  SEVERITY_LABEL,
  STATUS_LABEL,
  isComplaintOverdue,
  COMPLAINT_CATEGORIES,
} from "./complaintsUi";

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

export function ComplaintDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [complaint, setComplaint] = useState<ComplaintDetail | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setState("loading");
      try {
        setComplaint(await api.complaints.get(id));
        setState("ready");
      } catch (err) {
        if (!opts?.silent) setState(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
      }
    },
    [id],
  );
  const refresh = useCallback(() => load({ silent: true }), [load]);
  useEffect(() => {
    void load();
  }, [load]);

  if (state === "loading") return <Loader shape="panel" className="p-6" />;
  if (state === "notfound") return <p className="p-6 text-center text-ele-body">That complaint could not be found.</p>;
  if (state === "error" || !complaint) return <p role="alert" className="p-6 text-center text-ele-strong">Could not load this complaint. Please try again.</p>;

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <RecordHeader
        className="mb-4"
        title={complaint.ref}
        backTo="/office/complaints"
        backLabel="Back to complaints"
        status={
          <>
            <StatusPill domain="complaint" status={complaint.status} />
            <StatusPill domain="complaintSeverity" status={complaint.severity} />
            <span className="text-sm text-ele-body">Raised {formatDate(complaint.dateRaised)}</span>
          </>
        }
      />

      {isComplaintOverdue(complaint) && (
        <div role="alert" className="mb-4 rounded-ele border border-ele-danger bg-red-500/10 p-3 text-sm">
          <p className="font-heading uppercase tracking-wide text-ele-danger">Past target resolution date</p>
          <p className="mt-1 text-ele-body">
            The target resolution date ({formatDate(complaint.targetResolutionDate)}) has passed and this complaint is not yet resolved or closed.
          </p>
        </div>
      )}

      <StatusPanel complaint={complaint} onChanged={refresh} />
      <EditPanel complaint={complaint} onSaved={refresh} />
      <DeleteSection complaint={complaint} onDeleted={() => navigate("/office/complaints")} />
    </div>
  );
}

function StatusPanel({ complaint, onChanged }: { complaint: ComplaintDetail; onChanged: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasCorrectiveAction = Boolean(complaint.correctiveAction && complaint.correctiveAction.trim());

  async function move(status: ComplaintStatus) {
    setBusy(true);
    setError(null);
    try {
      await api.complaints.update(complaint.id, { status });
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not update the status.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="mb-2 font-heading text-lg text-ele-strong">Status</h2>
      {complaint.allowedNextStatuses.length === 0 ? (
        <p className="text-sm text-ele-body">This complaint is closed. No further status changes are possible.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-ele-body">Move forward to:</span>
          {complaint.allowedNextStatuses.map((s) => {
            const blockedClose = s === "closed" && !hasCorrectiveAction;
            return (
              <button
                key={s}
                disabled={busy || blockedClose}
                title={blockedClose ? "Record a corrective action before closing this complaint." : undefined}
                onClick={() => move(s)}
                className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-40"
              >
                {STATUS_LABEL[s]}
              </button>
            );
          })}
        </div>
      )}
      {!hasCorrectiveAction && complaint.status !== "closed" && (
        <p className="mt-2 text-xs text-ele-body">A corrective action must be recorded below before a complaint can be closed.</p>
      )}
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}

function EditPanel({ complaint, onSaved }: { complaint: ComplaintDetail; onSaved: () => Promise<void> }) {
  // The customer + responsible-person pickers load through the shared options loader so a failed
  // fetch shows an inline "Could not load. Retry" instead of a silently empty dropdown
  // (Session AEH, FIX-2 B3).
  const ownersState = useOptionsLoader<ProspectOwner[]>(() => api.prospects.owners(), []);
  const owners = ownersState.data ?? [];
  const [form, setForm] = useState(seed(complaint));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(seed(complaint));
  }, [complaint]);

  const set =
    (name: keyof ReturnType<typeof seed>) =>
    (e: { target: { value: string } }) => {
      setForm((f) => ({ ...f, [name]: e.target.value }));
      setSaved(false);
    };

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.complaints.update(complaint.id, {
        source: form.source as ComplaintSource,
        severity: form.severity as ComplaintSeverity,
        customerId: form.customerId || null,
        description: form.description.trim() || undefined,
        category: form.category.trim() || null,
        rootCause: form.rootCause.trim() || null,
        correctiveAction: form.correctiveAction.trim() || null,
        preventiveAction: form.preventiveAction.trim() || null,
        responsibleUserId: form.responsibleUserId || null,
        targetResolutionDate: form.targetResolutionDate || null,
        notes: form.notes.trim() || null,
      });
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="mb-3 font-heading text-lg text-ele-strong">Details and corrective action</h2>
      <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
        <label>
          <span className={labelCls}>Source</span>
          <select aria-label="Source" value={form.source} onChange={set("source")} className={inputCls}>
            {(["customer", "third_party", "internal", "regulatory"] as ComplaintSource[]).map((s) => (
              <option key={s} value={s}>
                {SOURCE_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className={labelCls}>Severity</span>
          <select aria-label="Severity" value={form.severity} onChange={set("severity")} className={inputCls}>
            {(["low", "medium", "high", "critical"] as ComplaintSeverity[]).map((s) => (
              <option key={s} value={s}>
                {SEVERITY_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <div>
          <RecordPicker
            kind="customer"
            label="Customer"
            value={form.customerId}
            placeholder="Search customers (leave empty if not in the system)"
            onChange={(id) => setForm((f) => ({ ...f, customerId: id }))}
          />
        </div>
        <label>
          <span className={labelCls}>Responsible person</span>
          <select aria-label="Responsible person" value={form.responsibleUserId} onChange={set("responsibleUserId")} className={inputCls}>
            <option value="">Unassigned</option>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>
                {o.displayName}
              </option>
            ))}
          </select>
          {ownersState.error && (
            <p className="mt-1 text-xs text-ele-danger">
              Could not load people.{" "}
              <button type="button" onClick={ownersState.retry} className="underline hover:text-ele-strong">
                Retry
              </button>
            </p>
          )}
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>What happened</span>
          <textarea aria-label="Description" value={form.description} onChange={set("description")} rows={3} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Category</span>
          <input aria-label="Category" list="complaint-categories" value={form.category} onChange={set("category")} className={inputCls} />
          <datalist id="complaint-categories">
            {COMPLAINT_CATEGORIES.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </label>
        <label>
          <span className={labelCls}>Target resolution date</span>
          <input aria-label="Target resolution date" type="date" value={form.targetResolutionDate} onChange={set("targetResolutionDate")} className={inputCls} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Root cause</span>
          <textarea aria-label="Root cause" value={form.rootCause} onChange={set("rootCause")} rows={2} className={inputCls} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Corrective action (required to close)</span>
          <textarea aria-label="Corrective action" value={form.correctiveAction} onChange={set("correctiveAction")} rows={2} className={inputCls} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Preventive action</span>
          <textarea aria-label="Preventive action" value={form.preventiveAction} onChange={set("preventiveAction")} rows={2} className={inputCls} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Notes</span>
          <textarea aria-label="Notes" value={form.notes} onChange={set("notes")} rows={2} className={inputCls} />
        </label>
        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
          {saved && (
            <span role="status" className="text-sm text-ele-green">
              Saved.
            </span>
          )}
        </div>
      </form>
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}

function DeleteSection({ complaint, onDeleted }: { complaint: ComplaintDetail; onDeleted: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Mirror the server rule: an untouched open complaint hard-deletes; anything investigated
  // (a corrective action recorded, or progressed beyond open) archives (status = closed).
  const willArchive = complaint.status !== "open" || Boolean(complaint.correctiveAction && complaint.correctiveAction.trim());

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api.complaints.remove(complaint.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not delete this complaint.");
      setBusy(false);
    }
  }

  return (
    <section className="rounded-ele border border-ele-hairline bg-ele-surface-soft p-5">
      <Button
        variant="destructive"
        size="sm"
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        {willArchive ? "Archive complaint" : "Delete complaint"}
      </Button>
      <ConfirmDeleteModal
        open={open}
        variant={willArchive ? "archive" : "destructive"}
        title={willArchive ? "Archive this complaint?" : "Delete this complaint?"}
        message={
          willArchive
            ? "This complaint has corrective-action history, so it will be archived (closed) for the audit trail and kept, not deleted."
            : "This complaint is still open with no corrective action recorded, so it will be permanently deleted. This cannot be undone."
        }
        confirmLabel={willArchive ? "Archive complaint" : "Delete complaint"}
        busy={busy}
        error={error}
        onConfirm={confirm}
        onCancel={() => {
          if (!busy) {
            setOpen(false);
            setError(null);
          }
        }}
      />
    </section>
  );
}

// Seed the edit form from a complaint (empty strings for null fields).
function seed(c: ComplaintDetail) {
  return {
    source: c.source as string,
    severity: c.severity as string,
    customerId: c.customerId ?? "",
    description: c.description,
    category: c.category ?? "",
    rootCause: c.rootCause ?? "",
    correctiveAction: c.correctiveAction ?? "",
    preventiveAction: c.preventiveAction ?? "",
    responsibleUserId: c.responsibleUserId ?? "",
    targetResolutionDate: c.targetResolutionDate ? c.targetResolutionDate.slice(0, 10) : "",
    notes: c.notes ?? "",
  };
}
